const API_BASE = process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

async function authFetch(path: string, token: string, options: RequestInit = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...options.headers },
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

// ─── Types ──────────────────────────────────────────────────────────────────

export interface OptionsEngine {
  engineId: string;
  name: string;
  status: 'active' | 'paused';
  brokerType: 'tastytrade';
  paperMode: boolean;
  autoExecuteEnabled: boolean;
  tastytradeAccountNumber: string;
  tastytradeConnectionId?: string;
  optionsConfig: {
    symbol: string;
    tradeType?: 'spread' | 'naked' | 'auto';
    fillStrategy?: 'walk' | 'patient';
    maxOrderAgeHours?: number;
    horizons: number[];
    spreadWidth: number;
    quantity: number;
    profitTargetPct: number;
    stopLossMultiplier: number;
    hardStopMultiplier?: number;
    enableHardStopUnderlying?: boolean;
    hardStopUnderlyingBuffer?: number;
    exitStrategy?: 'baseline' | 'convert';
    maxOpenPositions: number;
    maxDailyTrades: number;
    maxBpPct: number;
    minSpreadCredit?: number;
    fillWalkSteps?: number;
    fillWalkIntervalMs?: number;
    fillAdverseTicks?: number;
    horizonStrategy?: 'lowest_dte' | 'highest_score' | 'highest_credit' | 'best_theta';
    profitCloseMode?: 'smart' | 'strict';
    postProfitCooldownHours?: number;
    lossCloseMode?: 'auto' | 'alerts';
    stopLossMinDte?: number;
    signalSource?: 'v2' | 'notches';
    preset?: 'conservative' | 'balanced' | 'aggressive';
    notchesMaxDte?: number;
    notchesMinDte?: number;
    notchesStrategy?: 'naked_put' | 'bear_call_spread' | 'jade_lizard' | 'iron_condor';
    hedgeWithLongCall?: boolean;
    hedgeCallTargetDelta?: number;
    hedgeCallMinDTE?: number;
    creditDegradationGate?: {
      enabled: boolean;
      maxPctBelowSignal: number;
      onBreach: 'skip' | 'proceed';
    };
  };
  lastProfitCloseAt?: string;
  createdAt: string;
}

export interface OptionsTrade {
  tradeId: string;
  engineId: string;
  underlying: string;
  strategy: string;
  optionType?: 'C' | 'P';
  sellContractSymbol?: string;
  buyContractSymbol?: string;
  paperMode: boolean;
  status: 'pending' | 'submitted' | 'open' | 'closed' | 'cancelled' | 'failed';
  sellStrike: number;
  buyStrike: number;
  spreadWidth: number;
  expiration: string;
  credit: number;
  quantity: number;
  multiplier: number;
  maxProfit: number;
  maxLoss: number;
  profitTargetPrice: number;
  profitTargetPct: number;
  stopLossPrice: number;
  stopLossMultiplier: number;
  spotAtEntry: number;
  horizon: number;
  delta: number;
  theta: number;
  iv: number;
  probProfit: number;
  score: number;
  skewRegime: string;
  // Account info
  accountAtEntry?: {
    accountNumber: string;
    netLiquidatingValue: number;
    derivativeBuyingPower: number;
    cashBalance: number;
  };
  dryRunResult?: {
    valid: boolean;
    buyingPowerEffect?: any;
    warnings?: string[];
    error?: string;
  };
  // Fill
  filledCredit?: number;
  // Tracking
  currentSpreadValue?: number;
  unrealizedPnl?: number;
  daysHeld?: number;
  lastCheckedAt?: string;
  // Exit
  realizedPnl?: number;
  exitPrice?: number;
  exitAt?: string;
  exitReason?: string;
  holdDays?: number;
  pnlPct?: number;
  fees?: number;
  grossPnl?: number;
  reconciled?: boolean;
  createdAt: string;
}

export interface TradeAnalytics {
  totalTrades: number;
  openTrades: number;
  closedTrades: number;
  wins: number;
  losses: number;
  winRate: number;
  totalPnl: number;       // realized — sum of realizedPnl on closed trades (kept for back-compat)
  realizedPnl: number;    // alias of totalPnl, named for clarity
  unrealizedPnl: number;  // mark-to-market on open auto-trader positions
  netPnl: number;         // realizedPnl + unrealizedPnl
  avgPnl: number;
  avgWin: number;
  avgLoss: number;
  avgHoldDays: number;
  profitFactor: number;
  maxDrawdown: number;
  bestTrade: number;
  worstTrade: number;
  byHorizon: Record<number, { trades: number; winRate: number; avgPnl: number }>;
  byExitReason: Record<string, { count: number; avgPnl: number }>;
  bySkewRegime: Record<string, { trades: number; winRate: number; avgPnl: number }>;
  equityCurve: { date: string; pnl: number; cumPnl: number }[];
}

// ─── API calls ──────────────────────────────────────────────────────────────

export interface TastyTradeConnection {
  connectionId: string;
  label: string;
  accountNumber: string;
  accountType?: string;
  marginOrCash?: string;
  credentialKey: string;
  createdAt: string;
}

export async function getTastyTradeConnections(token: string): Promise<TastyTradeConnection[]> {
  const data = await authFetch('/user/tastytrade/connections', token);
  return data.connections || [];
}

export async function getOptionsEngines(token: string): Promise<OptionsEngine[]> {
  const data = await authFetch('/algo/engines', token);
  return (data.engines || []).filter((e: any) => e.brokerType === 'tastytrade');
}

export interface CreateOptionsEngineRequest {
  name: string;
  symbol: string;
  tradeType?: 'spread' | 'naked' | 'auto';
  fillStrategy?: 'walk' | 'patient';
  maxOrderAgeHours?: number;
  horizons: number[];
  spreadWidth: number;
  quantity: number;
  profitTargetPct: number;
  stopLossMultiplier: number;
  hardStopMultiplier?: number;
  enableHardStopUnderlying?: boolean;
  hardStopUnderlyingBuffer?: number;
  exitStrategy?: 'baseline' | 'convert';
  maxOpenPositions: number;
  maxDailyTrades: number;
  maxBpPct: number;
  minSpreadCredit?: number | null;
  fillWalkSteps?: number;
  fillWalkIntervalMs?: number;
  fillAdverseTicks?: number;
  horizonStrategy?: 'lowest_dte' | 'highest_score' | 'highest_credit' | 'best_theta';
  profitCloseMode?: 'smart' | 'strict';
  postProfitCooldownHours?: number;
  lossCloseMode?: 'auto' | 'alerts';
  stopLossMinDte?: number;
  signalSource?: 'v2' | 'notches';
  preset?: 'conservative' | 'balanced' | 'aggressive';
  notchesMaxDte?: number;
  notchesMinDte?: number;
  notchesStrategy?: 'naked_put' | 'bear_call_spread' | 'jade_lizard' | 'iron_condor';
  hedgeWithLongCall?: boolean;
  hedgeCallTargetDelta?: number;
  hedgeCallMinDTE?: number;
  creditDegradationGate?: {
    enabled: boolean;
    maxPctBelowSignal: number;
    onBreach: 'skip' | 'proceed';
  };
  paperMode: boolean;
  schedule: string;
  connectionId?: string;
  accountNumber?: string;
}

export async function createOptionsEngine(token: string, body: CreateOptionsEngineRequest): Promise<OptionsEngine> {
  const data = await authFetch('/options-trader/engines', token, { method: 'POST', body: JSON.stringify(body) });
  return data.engine;
}

export async function updateOptionsEngine(token: string, engineId: string, body: Partial<CreateOptionsEngineRequest & { status: string }>): Promise<void> {
  await authFetch(`/options-trader/engines/${engineId}`, token, { method: 'PUT', body: JSON.stringify(body) });
}

export async function deleteOptionsEngine(token: string, engineId: string): Promise<void> {
  await authFetch(`/options-trader/engines/${engineId}`, token, { method: 'DELETE' });
}

export async function getOptionsTrades(token: string, params?: { engineId?: string; status?: string; limit?: number }): Promise<OptionsTrade[]> {
  const query = new URLSearchParams({ source: 'trades' });
  if (params?.engineId) query.set('engineId', params.engineId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', String(params.limit));
  const data = await authFetch(`/algo/signals?${query.toString()}`, token);
  return (data.signals || []).filter((t: any) => t.assetClass === 'options');
}

export async function getAllOptionsTrades(token: string): Promise<OptionsTrade[]> {
  const data = await authFetch('/algo/signals?source=trades&limit=500', token);
  return (data.signals || []).filter((t: any) => t.assetClass === 'options');
}

export async function reconcileTrades(token: string, dryRun: boolean = false): Promise<any> {
  return authFetch('/options-trader/reconcile', token, {
    method: 'POST',
    body: JSON.stringify({ dryRun }),
  });
}

export function computeAnalytics(trades: OptionsTrade[]): TradeAnalytics {
  const closed = trades.filter(t => t.status === 'closed' && !t.paperMode);
  const open = trades.filter(t => ['open', 'submitted'].includes(t.status) && !t.paperMode);
  const wins = closed.filter(t => (t.realizedPnl || 0) > 0);
  const losses = closed.filter(t => (t.realizedPnl || 0) <= 0);

  const totalPnl = closed.reduce((sum, t) => sum + (t.realizedPnl || 0), 0);
  const avgPnl = closed.length > 0 ? totalPnl / closed.length : 0;
  const avgWin = wins.length > 0 ? wins.reduce((s, t) => s + (t.realizedPnl || 0), 0) / wins.length : 0;
  const avgLoss = losses.length > 0 ? losses.reduce((s, t) => s + (t.realizedPnl || 0), 0) / losses.length : 0;
  const avgHoldDays = closed.length > 0 ? closed.reduce((s, t) => s + (t.holdDays || 0), 0) / closed.length : 0;
  const grossWins = wins.reduce((s, t) => s + (t.realizedPnl || 0), 0);
  const grossLosses = Math.abs(losses.reduce((s, t) => s + (t.realizedPnl || 0), 0));
  const profitFactor = grossLosses > 0 ? grossWins / grossLosses : grossWins > 0 ? Infinity : 0;

  // Max drawdown
  let peak = 0, maxDD = 0, running = 0;
  const equityCurve: { date: string; pnl: number; cumPnl: number }[] = [];
  const sortedClosed = [...closed].sort((a, b) => (a.exitAt || '').localeCompare(b.exitAt || ''));
  for (const t of sortedClosed) {
    running += t.realizedPnl || 0;
    peak = Math.max(peak, running);
    maxDD = Math.min(maxDD, running - peak);
    const exitDate = t.exitAt || t.createdAt || '';
    const dateLabel = new Date(exitDate).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    equityCurve.push({ date: dateLabel, pnl: t.realizedPnl || 0, cumPnl: Math.round(running * 100) / 100 });
  }

  // By horizon
  const byHorizon: Record<number, { trades: number; winRate: number; avgPnl: number }> = {};
  for (const t of closed) {
    const h = t.horizon || 0;
    if (!byHorizon[h]) byHorizon[h] = { trades: 0, winRate: 0, avgPnl: 0 };
    byHorizon[h].trades++;
  }
  for (const h of Object.keys(byHorizon)) {
    const hn = Number(h);
    const hTrades = closed.filter(t => t.horizon === hn);
    const hWins = hTrades.filter(t => (t.realizedPnl || 0) > 0);
    byHorizon[hn].winRate = hTrades.length > 0 ? Math.round((hWins.length / hTrades.length) * 100) : 0;
    byHorizon[hn].avgPnl = hTrades.length > 0 ? Math.round(hTrades.reduce((s, t) => s + (t.realizedPnl || 0), 0) / hTrades.length) : 0;
  }

  // By exit reason
  const byExitReason: Record<string, { count: number; avgPnl: number }> = {};
  for (const t of closed) {
    const r = t.exitReason || 'unknown';
    if (!byExitReason[r]) byExitReason[r] = { count: 0, avgPnl: 0 };
    byExitReason[r].count++;
  }
  for (const r of Object.keys(byExitReason)) {
    const rTrades = closed.filter(t => (t.exitReason || 'unknown') === r);
    byExitReason[r].avgPnl = rTrades.length > 0 ? Math.round(rTrades.reduce((s, t) => s + (t.realizedPnl || 0), 0) / rTrades.length) : 0;
  }

  // By skew regime
  const bySkewRegime: Record<string, { trades: number; winRate: number; avgPnl: number }> = {};
  for (const t of closed) {
    const s = t.skewRegime || 'unknown';
    if (!bySkewRegime[s]) bySkewRegime[s] = { trades: 0, winRate: 0, avgPnl: 0 };
    bySkewRegime[s].trades++;
  }
  for (const s of Object.keys(bySkewRegime)) {
    const sTrades = closed.filter(t => (t.skewRegime || 'unknown') === s);
    const sWins = sTrades.filter(t => (t.realizedPnl || 0) > 0);
    bySkewRegime[s].winRate = sTrades.length > 0 ? Math.round((sWins.length / sTrades.length) * 100) : 0;
    bySkewRegime[s].avgPnl = sTrades.length > 0 ? Math.round(sTrades.reduce((sum, t) => sum + (t.realizedPnl || 0), 0) / sTrades.length) : 0;
  }

  // Mark-to-market on open auto-trader positions. unrealizedPnl is set on
  // each trade row by the position monitor on every scan.
  const unrealizedPnl = open.reduce((s, t) => s + (Number(t.unrealizedPnl) || 0), 0);
  const realizedPnlRounded = Math.round(totalPnl * 100) / 100;
  const unrealizedPnlRounded = Math.round(unrealizedPnl * 100) / 100;

  return {
    totalTrades: trades.filter(t => !t.paperMode).length,
    openTrades: open.length,
    closedTrades: closed.length,
    wins: wins.length,
    losses: losses.length,
    winRate: closed.length > 0 ? Math.round((wins.length / closed.length) * 100) : 0,
    totalPnl: realizedPnlRounded,
    realizedPnl: realizedPnlRounded,
    unrealizedPnl: unrealizedPnlRounded,
    netPnl: Math.round((realizedPnlRounded + unrealizedPnlRounded) * 100) / 100,
    avgPnl: Math.round(avgPnl),
    avgWin: Math.round(avgWin),
    avgLoss: Math.round(avgLoss),
    avgHoldDays: Math.round(avgHoldDays * 10) / 10,
    profitFactor: Math.round(profitFactor * 100) / 100,
    maxDrawdown: Math.round(maxDD),
    bestTrade: closed.length > 0 ? Math.round(Math.max(...closed.map(t => t.realizedPnl || 0))) : 0,
    worstTrade: closed.length > 0 ? Math.round(Math.min(...closed.map(t => t.realizedPnl || 0))) : 0,
    byHorizon,
    byExitReason,
    bySkewRegime,
    equityCurve,
  };
}
